/**
 * Super Admin Redux Action Constants
 * 
 * These constants define all action types for Super Admin operations.
 * Used by actions and reducers to maintain consistent action type strings.
 * 
 * Naming Convention:
 * - REQUEST: Action dispatched when API call starts
 * - SUCCESS: Action dispatched when API call succeeds
 * - FAIL: Action dispatched when API call fails
 * - RESET: Action to reset state to initial values
 * 
 * @module SuperAdmin
 * @category Constants
 */

// ============================================================================
// PLATFORM ANALYTICS
// ============================================================================

/** Fetch platform-wide analytics */
export const PLATFORM_ANALYTICS_REQUEST = 'PLATFORM_ANALYTICS_REQUEST';
export const PLATFORM_ANALYTICS_SUCCESS = 'PLATFORM_ANALYTICS_SUCCESS';
export const PLATFORM_ANALYTICS_FAIL = 'PLATFORM_ANALYTICS_FAIL';

// ============================================================================
// TENANT MANAGEMENT
// ============================================================================

/** Fetch all tenants list */
export const TENANT_LIST_REQUEST = 'TENANT_LIST_REQUEST';
export const TENANT_LIST_SUCCESS = 'TENANT_LIST_SUCCESS';
export const TENANT_LIST_FAIL = 'TENANT_LIST_FAIL';

/** Fetch single tenant details */
export const TENANT_DETAILS_REQUEST = 'TENANT_DETAILS_REQUEST';
export const TENANT_DETAILS_SUCCESS = 'TENANT_DETAILS_SUCCESS';
export const TENANT_DETAILS_FAIL = 'TENANT_DETAILS_FAIL';
export const TENANT_DETAILS_RESET = 'TENANT_DETAILS_RESET';

/** Fetch paginated tenant users */
export const TENANT_USERS_REQUEST = 'TENANT_USERS_REQUEST';
export const TENANT_USERS_SUCCESS = 'TENANT_USERS_SUCCESS';
export const TENANT_USERS_FAIL = 'TENANT_USERS_FAIL';

/** Fetch tenant analytics */
export const TENANT_ANALYTICS_REQUEST = 'TENANT_ANALYTICS_REQUEST';
export const TENANT_ANALYTICS_SUCCESS = 'TENANT_ANALYTICS_SUCCESS';
export const TENANT_ANALYTICS_FAIL = 'TENANT_ANALYTICS_FAIL';

/** Create new tenant with state admin */
export const TENANT_CREATE_REQUEST = 'TENANT_CREATE_REQUEST';
export const TENANT_CREATE_SUCCESS = 'TENANT_CREATE_SUCCESS';
export const TENANT_CREATE_FAIL = 'TENANT_CREATE_FAIL';
export const TENANT_CREATE_RESET = 'TENANT_CREATE_RESET';

/** Update tenant status (suspend/activate) */
export const TENANT_STATUS_UPDATE_REQUEST = 'TENANT_STATUS_UPDATE_REQUEST';
export const TENANT_STATUS_UPDATE_SUCCESS = 'TENANT_STATUS_UPDATE_SUCCESS';
export const TENANT_STATUS_UPDATE_FAIL = 'TENANT_STATUS_UPDATE_FAIL';
export const TENANT_STATUS_UPDATE_RESET = 'TENANT_STATUS_UPDATE_RESET';

// ============================================================================
// AUDIT LOGS
// ============================================================================

/** Fetch audit logs */
export const AUDIT_LOGS_REQUEST = 'AUDIT_LOGS_REQUEST';
export const AUDIT_LOGS_SUCCESS = 'AUDIT_LOGS_SUCCESS';
export const AUDIT_LOGS_FAIL = 'AUDIT_LOGS_FAIL';

/** Fetch audit statistics */
export const AUDIT_STATS_REQUEST = 'AUDIT_STATS_REQUEST';
export const AUDIT_STATS_SUCCESS = 'AUDIT_STATS_SUCCESS';
export const AUDIT_STATS_FAIL = 'AUDIT_STATS_FAIL';

// ============================================================================
// PLATFORM USERS
// ============================================================================

export const PLATFORM_USER_LIST_REQUEST = 'PLATFORM_USER_LIST_REQUEST';
export const PLATFORM_USER_LIST_SUCCESS = 'PLATFORM_USER_LIST_SUCCESS';
export const PLATFORM_USER_LIST_FAIL = 'PLATFORM_USER_LIST_FAIL';

export const PLATFORM_USER_CREATE_REQUEST = 'PLATFORM_USER_CREATE_REQUEST';
export const PLATFORM_USER_CREATE_SUCCESS = 'PLATFORM_USER_CREATE_SUCCESS';
export const PLATFORM_USER_CREATE_FAIL = 'PLATFORM_USER_CREATE_FAIL';
export const PLATFORM_USER_CREATE_RESET = 'PLATFORM_USER_CREATE_RESET';

export const PLATFORM_USER_UPDATE_REQUEST = 'PLATFORM_USER_UPDATE_REQUEST';
export const PLATFORM_USER_UPDATE_SUCCESS = 'PLATFORM_USER_UPDATE_SUCCESS';
export const PLATFORM_USER_UPDATE_FAIL = 'PLATFORM_USER_UPDATE_FAIL';
export const PLATFORM_USER_UPDATE_RESET = 'PLATFORM_USER_UPDATE_RESET';

export const PLATFORM_USER_STATUS_REQUEST = 'PLATFORM_USER_STATUS_REQUEST';
export const PLATFORM_USER_STATUS_SUCCESS = 'PLATFORM_USER_STATUS_SUCCESS';
export const PLATFORM_USER_STATUS_FAIL = 'PLATFORM_USER_STATUS_FAIL';
export const PLATFORM_USER_STATUS_RESET = 'PLATFORM_USER_STATUS_RESET';

// ============================================================================
// OWNER REPORTS
// ============================================================================

export const TENANT_COMMERCIAL_TERMS_REQUEST = 'TENANT_COMMERCIAL_TERMS_REQUEST';
export const TENANT_COMMERCIAL_TERMS_SUCCESS = 'TENANT_COMMERCIAL_TERMS_SUCCESS';
export const TENANT_COMMERCIAL_TERMS_FAIL = 'TENANT_COMMERCIAL_TERMS_FAIL';

export const TENANT_COMMERCIAL_TERMS_UPDATE_REQUEST = 'TENANT_COMMERCIAL_TERMS_UPDATE_REQUEST';
export const TENANT_COMMERCIAL_TERMS_UPDATE_SUCCESS = 'TENANT_COMMERCIAL_TERMS_UPDATE_SUCCESS';
export const TENANT_COMMERCIAL_TERMS_UPDATE_FAIL = 'TENANT_COMMERCIAL_TERMS_UPDATE_FAIL';
export const TENANT_COMMERCIAL_TERMS_UPDATE_RESET = 'TENANT_COMMERCIAL_TERMS_UPDATE_RESET';

export const OWNER_REPORT_CONFIG_LIST_REQUEST = 'OWNER_REPORT_CONFIG_LIST_REQUEST';
export const OWNER_REPORT_CONFIG_LIST_SUCCESS = 'OWNER_REPORT_CONFIG_LIST_SUCCESS';
export const OWNER_REPORT_CONFIG_LIST_FAIL = 'OWNER_REPORT_CONFIG_LIST_FAIL';

export const OWNER_REPORT_CONFIG_DETAILS_REQUEST = 'OWNER_REPORT_CONFIG_DETAILS_REQUEST';
export const OWNER_REPORT_CONFIG_DETAILS_SUCCESS = 'OWNER_REPORT_CONFIG_DETAILS_SUCCESS';
export const OWNER_REPORT_CONFIG_DETAILS_FAIL = 'OWNER_REPORT_CONFIG_DETAILS_FAIL';
export const OWNER_REPORT_CONFIG_DETAILS_RESET = 'OWNER_REPORT_CONFIG_DETAILS_RESET';

export const OWNER_REPORT_CONFIG_CREATE_REQUEST = 'OWNER_REPORT_CONFIG_CREATE_REQUEST';
export const OWNER_REPORT_CONFIG_CREATE_SUCCESS = 'OWNER_REPORT_CONFIG_CREATE_SUCCESS';
export const OWNER_REPORT_CONFIG_CREATE_FAIL = 'OWNER_REPORT_CONFIG_CREATE_FAIL';
export const OWNER_REPORT_CONFIG_CREATE_RESET = 'OWNER_REPORT_CONFIG_CREATE_RESET';

export const OWNER_REPORT_CONFIG_UPDATE_REQUEST = 'OWNER_REPORT_CONFIG_UPDATE_REQUEST';
export const OWNER_REPORT_CONFIG_UPDATE_SUCCESS = 'OWNER_REPORT_CONFIG_UPDATE_SUCCESS';
export const OWNER_REPORT_CONFIG_UPDATE_FAIL = 'OWNER_REPORT_CONFIG_UPDATE_FAIL';
export const OWNER_REPORT_CONFIG_UPDATE_RESET = 'OWNER_REPORT_CONFIG_UPDATE_RESET';

export const OWNER_REPORT_CONFIG_STATUS_REQUEST = 'OWNER_REPORT_CONFIG_STATUS_REQUEST';
export const OWNER_REPORT_CONFIG_STATUS_SUCCESS = 'OWNER_REPORT_CONFIG_STATUS_SUCCESS';
export const OWNER_REPORT_CONFIG_STATUS_FAIL = 'OWNER_REPORT_CONFIG_STATUS_FAIL';
export const OWNER_REPORT_CONFIG_STATUS_RESET = 'OWNER_REPORT_CONFIG_STATUS_RESET';

export const OWNER_REPORT_RUN_NOW_REQUEST = 'OWNER_REPORT_RUN_NOW_REQUEST';
export const OWNER_REPORT_RUN_NOW_SUCCESS = 'OWNER_REPORT_RUN_NOW_SUCCESS';
export const OWNER_REPORT_RUN_NOW_FAIL = 'OWNER_REPORT_RUN_NOW_FAIL';
export const OWNER_REPORT_RUN_NOW_RESET = 'OWNER_REPORT_RUN_NOW_RESET';

export const OWNER_REPORT_RUNS_REQUEST = 'OWNER_REPORT_RUNS_REQUEST';
export const OWNER_REPORT_RUNS_SUCCESS = 'OWNER_REPORT_RUNS_SUCCESS';
export const OWNER_REPORT_RUNS_FAIL = 'OWNER_REPORT_RUNS_FAIL';

export const OWNER_REPORT_RUN_DETAILS_REQUEST = 'OWNER_REPORT_RUN_DETAILS_REQUEST';
export const OWNER_REPORT_RUN_DETAILS_SUCCESS = 'OWNER_REPORT_RUN_DETAILS_SUCCESS';
export const OWNER_REPORT_RUN_DETAILS_FAIL = 'OWNER_REPORT_RUN_DETAILS_FAIL';
export const OWNER_REPORT_RUN_DETAILS_RESET = 'OWNER_REPORT_RUN_DETAILS_RESET';
